import { ResolveFn } from '@angular/router';
import { DestroyRef, inject } from '@angular/core';
import { PoService } from '../../services/po/po.service';
import { PODetailsResponse } from '../../models/po';
import { forkJoin, map } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonService } from '../../services/common/common.service';
import { BankAccount } from '../../Interfaces/bankAccount.interface';
import {
  findPurchaseOrder,
  toPoDetails,
} from '../../pages/po-details/po-rfq.adapter';

interface PoAllDetails {
  details: PODetailsResponse;
  /**
   * Typed off what the endpoint actually answers rather than `POBankDetails`:
   * the screen reads `bankDetails.bank_account`, and the service's own return
   * type has never matched that.
   */
  bankDetails: { bank_account?: BankAccount } | BankAccount;
}

/**
 * The supplier's purchase-order route still takes a purchase-order id and reads
 * a single purchase order, which the contract leaves untouched.
 *
 * Split from the buyer's: theirs is now keyed by RFQ, and a fork on the URL was
 * hiding two genuinely different contracts behind one return type.
 */
export const supplierPoDetailsResolver: ResolveFn<
  Pick<PoAllDetails, 'details'>
> = route => {
  const poService = inject(PoService);
  const destroyRef = inject(DestroyRef);

  return forkJoin({
    details: poService.getSupplierPoDetails(route.params['id']),
  }).pipe(takeUntilDestroyed(destroyRef));
};

/**
 * One purchase order for the buyer, taken out of its RFQ's payload.
 *
 * There is no endpoint left that answers a single buyer purchase order — the
 * details route is keyed by RFQ — so this reads the RFQ and picks the order the
 * nested route names, then adapts it to the shape the screen renders.
 */
export const buyerRfqSinglePoResolver: ResolveFn<PoAllDetails> = route => {
  const poService = inject(PoService);
  const commonService = inject(CommonService);
  const destroyRef = inject(DestroyRef);

  const rfqId = Number(route.params['id']);
  const poId = Number(route.params['poId']);

  return forkJoin({
    bankDetails: commonService.getContractorBankDetails(),
    rfqDetails: poService.getBuyerRfqPoDetails(rfqId),
  }).pipe(
    takeUntilDestroyed(destroyRef),
    map(({ bankDetails, rfqDetails }) => {
      const rfq = rfqDetails?.rfq;
      const purchaseOrder = findPurchaseOrder(rfq, poId);

      return {
        bankDetails,
        details: {
          purchase_order:
            rfq && purchaseOrder ? toPoDetails(rfq, purchaseOrder) : undefined,
        },
      } as PoAllDetails;
    })
  );
};
